import { apiSlice } from 'api/apiSlice'
import { HistoricalOrder } from 'shared/types/HistoricalOrder'
import { Order } from 'shared/types/Order'

type GetPriceEstimate = {
  'address[city]'?: string
  'address[countryCode]'?: string
  'address[postalCode]'?: string
  'address[state]'?: string
  'order[instantShip]'?: string
  'order[productId]'?: number
  'order[productTemplateId]'?: number
  'order[shippingServiceLevel]'?: string
  'order[size]'?: number
  isAuthenticated?: boolean
  orderNumber?: number
}

const ordersApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    buyOrder: build.mutation({
      query: ({ orderNumber, ...body }) => ({
        app: 'sneakers',
        data: body,
        method: 'PUT',
        url: `/web-api/v1/orders/${orderNumber}/buy`,
      }),
    }),
    createAfterPayToken: build.mutation({
      query: ({ orderNumber, ...body }) => ({
        app: 'sneakers',
        data: body,
        method: 'POST',
        url: `/web-api/v1/orders/${orderNumber}/afterpay`,
      }),
    }),
    createKlarnaPaymentSession: build.mutation({
      query: ({ orderNumber, ...body }) => ({
        app: 'sneakers',
        data: body,
        method: 'POST',
        url: `/web-api/v1/orders/${orderNumber}/klarna`,
      }),
    }),
    createPayPalPaymentSession: build.mutation({
      query: ({ orderNumber, ...body }) => ({
        app: 'sneakers',
        data: body,
        method: 'POST',
        url: `/web-api/v1/orders/${orderNumber}/paypal`,
      }),
    }),
    createOrder: build.mutation({
      query: (body) => ({
        app: 'sneakers',
        data: body,
        method: 'POST',
        url: body?.order?.productId
          ? '/web-api/v1/orders'
          : '/web-api/v1/orders/create_for_product_template',
      }),
    }),
    getHistoricalOrders: build.query<{ orders: HistoricalOrder[] }, number | void>({
      providesTags: ['Order'],
      query: (page = 1) => ({
        app: 'sneakers',
        method: 'GET',
        url: `/web-api/v1/flight_club_historical_orders?page=${page}&timestamp=${new Date().getTime()}`,
      }),
    }),
    getOrder: build.query({
      providesTags: ['Order'],
      query: (orderNumber) => ({
        app: 'sneakers',
        cache: false,
        method: 'GET',
        url: `/web-api/v1/orders/${orderNumber}?includeCredits=true&timestamp=${new Date().getTime()}`,
      }),
    }),
    getPriceEstimate: build.query<Order, GetPriceEstimate>({
      query: ({ isAuthenticated, orderNumber, ...params }) => ({
        app: 'sneakers',
        cache: false,
        params,
        method: 'GET',
        url: isAuthenticated
          ? `/web-api/v1/orders/${orderNumber}/price_estimates`
          : `/web-api/v1/orders/guest_price_estimates`,
      }),
    }),
    requestOrderTransfer: build.mutation({
      query: (orderNumber) => ({
        app: 'sneakers',
        method: 'PUT',
        url: `/web-api/v1/orders/${orderNumber}/request_order_transfer`,
      }),
    }),
    updateOrder: build.mutation({
      invalidatesTags: ['Order'],
      query: ({ orderNumber, ...body }) => ({
        app: 'sneakers',
        data: body,
        method: 'PUT',
        url: `/web-api/v1/orders/${orderNumber}`,
      }),
    }),
    updateOrderStatus: build.mutation({
      invalidatesTags: ['Order'],
      query: ({ orderNumber, ...body }) => ({
        app: 'sneakers',
        data: body,
        method: 'PUT',
        url: `/web-api/v1/orders/${orderNumber}/update_status`,
      }),
    }),
    verifyBilling: build.mutation({
      query: ({ orderNumber, ...body }) => ({
        app: 'sneakers',
        data: body,
        method: 'POST',
        url: `/web-api/v1/orders/${orderNumber}/verify_billing`,
      }),
    }),
  }),
})

export const {
  useBuyOrderMutation,
  useCreateAfterPayTokenMutation,
  useCreateKlarnaPaymentSessionMutation,
  useCreateOrderMutation,
  useCreatePayPalPaymentSessionMutation,
  useGetHistoricalOrdersQuery,
  useGetOrderQuery,
  useLazyGetPriceEstimateQuery,
  useRequestOrderTransferMutation,
  useUpdateOrderMutation,
  useUpdateOrderStatusMutation,
  useVerifyBillingMutation,
} = ordersApi
